import { useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Rocket, Wand2, SlidersHorizontal, ArrowRight, type LucideIcon } from 'lucide-react';
import { PageTransition, StaggerItem } from '../app/page-transition.js';
import { PostCard, CARD_MEDIA, GRADIENT } from './post-card.js';
import { resolveAssetUrl } from './article/image-source.js';
import { formatDate } from '../../lib/format-price.js';
import { usePreferencesStore } from '../../stores/preferences.js';
import { getByCategory, getPostMeta } from './manifest.js';
import type { BlogMeta } from './types.js';

// The blog index is hand-arranged: a news hero, three pinned core guides, two
// section jump cards, one spotlight, then the full listings. The post thumbnail
// card + its media/icon config live in ./post-card so the article "Keep reading"
// rail reuses them.

// The article shown large with its video/image. The mask-editor post ships a
// short demo video, so it is the spotlight (video instead of a thumbnail).
const SPOTLIGHT_SLUG = 'mask-editor-inpaint-iterate';

// Three pinned learning guides. Dark grey cards with a small corner icon and
// the title inside; on hover a blurred article image is revealed (the install
// guide has no in-article image, so it reveals its accent gradient instead).
const CORE_CARDS = [
  {
    slug: 'install-agentbrush-claude-cursor',
    label: 'Getting started',
    blurb: 'Install AgentBrush and connect it to Claude or Cursor in about two minutes.',
    Icon: Rocket,
    gradient: GRADIENT.lime,
    image: undefined,
  },
  {
    slug: 'agentbrush-prompting-guide',
    label: 'Prompting guide',
    blurb: 'Write prompts that get you exactly the image you want, the first time.',
    Icon: Wand2,
    gradient: GRADIENT.cyan,
    image: 'images/prompt-dragon-village.png',
  },
  {
    slug: 'ai-image-generation-cost-tokens',
    label: 'Choosing the quality level',
    blurb: 'Tokens, quality tiers, and what each generation actually costs.',
    Icon: SlidersHorizontal,
    gradient: GRADIENT.purple,
    image: 'images/quality-crystal-high.png',
  },
] as const;

// ----------------------------------------------------------------------------
// Cards
// ----------------------------------------------------------------------------

function NewsHeroCard({
  post,
  image,
  dateLabel,
}: {
  post: BlogMeta;
  image: string | undefined;
  dateLabel: string;
}): JSX.Element {
  return (
    <Link
      to={`/blog/${post.slug}`}
      aria-label={post.title}
      className="group block overflow-hidden rounded-xl border border-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
    >
      <div className="relative aspect-[16/10] w-full standard:aspect-[21/8]">
        {image ? (
          <img
            src={resolveAssetUrl(image)}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.03]"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: GRADIENT.cyan }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-canvas/50 via-canvas/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 standard:p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-accent-cyan">
            Latest news
          </p>
          <h2 className="mt-2 max-w-[26ch] font-sans text-[clamp(22px,3.2vw,34px)] font-light leading-[1.1] tracking-[-0.02em] text-primary">
            {post.title}
          </h2>
          <p className="mt-2 line-clamp-2 max-w-[64ch] text-[15px] leading-[1.5] text-white/90">
            {post.description}
          </p>
          <p className="mt-3 font-mono text-[12px] text-white/65">{dateLabel}</p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2 text-[13px] font-semibold text-canvas">
            Read the article
            <ArrowRight
              className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}

function CoreCard({
  href,
  label,
  blurb,
  image,
  gradient,
  Icon,
}: {
  href: string;
  label: string;
  blurb: string;
  image: string | undefined;
  gradient: string;
  Icon: LucideIcon;
}): JSX.Element {
  return (
    <Link
      to={href}
      className="group relative block overflow-hidden rounded-xl border border-white/[0.06] bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
    >
      {/* Hover reveal: a blurred article image (or the accent gradient when the
          article has no image). A dark scrim keeps the title legible on top. */}
      {image ? (
        <img
          src={resolveAssetUrl(image)}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover opacity-0 blur-[14px] transition-opacity duration-300 group-hover:opacity-100"
        />
      ) : (
        <div
          className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: gradient }}
          aria-hidden="true"
        />
      )}
      <div
        className="absolute inset-0 bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden="true"
      />
      <div className="relative flex aspect-[16/10] flex-col justify-between p-5">
        <div className="flex justify-end">
          <Icon
            className="h-5 w-5 text-muted transition-colors duration-300 group-hover:text-white/85"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        </div>
        <div>
          <h3 className="font-sans text-[17px] font-semibold leading-[1.3] text-primary">
            {label}
          </h3>
          <p className="mt-1 text-[13px] leading-[1.45] text-secondary transition-colors duration-300 group-hover:text-white/80">
            {blurb}
          </p>
        </div>
      </div>
    </Link>
  );
}

function SectionJumpCard({
  title,
  blurb,
  image,
  href,
  buttonLabel,
  count,
}: {
  title: string;
  blurb: string;
  image: string;
  href: string;
  buttonLabel: string;
  count: number;
}): JSX.Element {
  // Both jump cards share a fixed height so they render identically; the text is
  // spread top-to-bottom (count at the top, button at the bottom) so the square
  // thumbnail still spans exactly from the count to the button.
  return (
    <div className="flex h-[176px] items-stretch gap-5 standard:gap-6">
      <div className="aspect-square h-full shrink-0 overflow-hidden rounded-2xl border border-white/[0.06]">
        <img
          src={resolveAssetUrl(image)}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            {count} {count === 1 ? 'article' : 'articles'}
          </p>
          <h3 className="font-sans text-[20px] font-light leading-[1.15] tracking-[-0.02em] text-primary">
            {title}
          </h3>
          <p className="text-[14px] leading-[1.5] text-secondary">{blurb}</p>
        </div>
        <a
          href={href}
          className="inline-flex w-fit items-center gap-2 rounded-md bg-accent-lime px-3.5 py-2 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {buttonLabel} →
        </a>
      </div>
    </div>
  );
}

function SpotlightVideo({
  src,
  poster,
  title,
}: {
  src: string;
  poster: string | undefined;
  title: string;
}): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  // Size the frame to the video's real aspect ratio once metadata loads, so the
  // demo shows in full (no crop, no letterbox bars). 16:9 is the pre-load guess.
  const [ratio, setRatio] = useState('16 / 9');
  return (
    <div
      className="relative w-full overflow-hidden rounded-xl border border-white/[0.06] bg-[#0c0c0c]"
      style={{ aspectRatio: ratio }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        controls={started}
        preload="metadata"
        playsInline
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.videoWidth > 0 && v.videoHeight > 0) {
            setRatio(`${v.videoWidth} / ${v.videoHeight}`);
          }
        }}
        onPlay={() => setStarted(true)}
        className={`absolute inset-0 h-full w-full object-cover transition-[filter,transform] duration-300 ${
          started ? '' : 'scale-[1.03] blur-[2px]'
        }`}
      />
      {!started ? (
        <button
          type="button"
          onClick={() => {
            void videoRef.current?.play();
          }}
          aria-label={`Play video: ${title}`}
          className="group/play absolute inset-0 flex items-center justify-center focus:outline-none"
        >
          {/* White rounded square with the play triangle knocked out (transparent),
              so the blurred video shows through the triangle. */}
          <svg
            width="60"
            height="60"
            viewBox="0 0 64 64"
            aria-hidden="true"
            className="drop-shadow-[0_4px_14px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover/play:scale-105"
          >
            <path
              fill="#ffffff"
              fillRule="evenodd"
              d="M16 0 H48 A16 16 0 0 1 64 16 V48 A16 16 0 0 1 48 64 H16 A16 16 0 0 1 0 48 V16 A16 16 0 0 1 16 0 Z M24 22 L41 32 L24 42 Z"
            />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

function SpotlightCard({
  post,
  media,
  dateLabel,
}: {
  post: BlogMeta;
  media: { image?: string; video?: string };
  dateLabel: string;
}): JSX.Element {
  return (
    <div>
      <h2 className="mb-4 font-sans text-[22px] font-light leading-[1.2] tracking-[-0.02em] text-primary">
        Featured
      </h2>
      {media.video ? (
        <SpotlightVideo
          src={resolveAssetUrl(media.video)}
          poster={media.image ? resolveAssetUrl(media.image) : undefined}
          title={post.title}
        />
      ) : media.image ? (
        <Link
          to={`/blog/${post.slug}`}
          aria-label={post.title}
          className="group relative block aspect-[16/9] w-full overflow-hidden rounded-xl border border-white/[0.06] bg-[#0c0c0c] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
        >
          <img
            src={resolveAssetUrl(media.image)}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.02]"
          />
        </Link>
      ) : null}
      <div className="mt-5 max-w-[72ch]">
        <Link
          to={`/blog/${post.slug}`}
          className="group inline-block rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
        >
          <h3 className="font-sans text-[clamp(20px,2.6vw,28px)] font-light leading-[1.15] tracking-[-0.02em] text-primary transition-colors group-hover:text-accent-cyan">
            {post.title}
          </h3>
        </Link>
        <p className="mt-2 text-[15px] leading-[1.6] text-secondary">{post.description}</p>
        <p className="mt-2 font-mono text-[12px] text-muted">{dateLabel}</p>
      </div>
    </div>
  );
}

function ShowcaseSection({
  id,
  title,
  posts,
  fmt,
}: {
  id: string;
  title: string;
  posts: BlogMeta[];
  fmt: (date: string) => string;
}): JSX.Element | null {
  if (posts.length === 0) return null;
  return (
    <StaggerItem>
      <section id={id} aria-labelledby={`${id}-heading`} className="mt-16 scroll-mt-24">
        <h2
          id={`${id}-heading`}
          className="mb-5 font-sans text-[22px] font-light leading-[1.2] tracking-[-0.02em] text-primary"
        >
          {title}
        </h2>
        <div className="grid grid-cols-1 gap-5 standard:grid-cols-2 wide:grid-cols-3">
          {posts.map((post) => (
            <PostCard key={post.slug} post={post} dateLabel={fmt(post.date)} />
          ))}
        </div>
      </section>
    </StaggerItem>
  );
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------

export function BlogPage() {
  const { t } = useTranslation('content');
  const locale = usePreferencesStore((s) => s.locale);
  const fmt = (date: string): string => formatDate(date, locale);

  const latestNews = getByCategory('news')[0];
  const spotlight = getPostMeta(SPOTLIGHT_SLUG);
  const useCases = getByCategory('use-case');
  const learning = getByCategory('learning');
  const comparison = getByCategory('comparison');
  const foundations = getByCategory('foundations');

  return (
    <PageTransition>
      <div className="px-6 pb-32 pt-12 standard:px-10">
        <StaggerItem>
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
            {t('blog.eyebrow')}
          </p>
          <h1 className="mt-4 font-sans text-[clamp(36px,6vw,56px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
            {t('blog.heading')}
          </h1>
          <p className="mb-10 mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
            {t('blog.subheadline')}
          </p>
        </StaggerItem>

        {latestNews ? (
          <StaggerItem>
            <NewsHeroCard
              post={latestNews}
              image={CARD_MEDIA[latestNews.slug]?.image}
              dateLabel={fmt(latestNews.date)}
            />
          </StaggerItem>
        ) : null}

        <StaggerItem>
          <div className="mt-5 grid grid-cols-1 gap-4 standard:grid-cols-3">
            {CORE_CARDS.map((card) => (
              <CoreCard
                key={card.slug}
                href={`/blog/${card.slug}`}
                label={card.label}
                blurb={card.blurb}
                image={card.image}
                gradient={card.gradient}
                Icon={card.Icon}
              />
            ))}
          </div>
        </StaggerItem>

        <StaggerItem>
          <div className="mt-12 grid grid-cols-1 gap-8 standard:grid-cols-2">
            <SectionJumpCard
              title="AgentBrush use cases"
              blurb="Real projects built from your agent: game art, app icons, product shots, marketing creatives, and more."
              image="images/product-tea-ad-story.png"
              href="#use-cases"
              buttonLabel="Browse use cases"
              count={useCases.length}
            />
            <SectionJumpCard
              title="Learning"
              blurb="Guides and explainers to get more out of every generation, from prompting to reference images."
              image="images/ref-robot-rooftop.png"
              href="#learning"
              buttonLabel="Browse learning"
              count={learning.length}
            />
          </div>
        </StaggerItem>

        {spotlight ? (
          <StaggerItem>
            <div className="mt-12">
              <SpotlightCard
                post={spotlight}
                media={CARD_MEDIA[spotlight.slug] ?? {}}
                dateLabel={fmt(spotlight.date)}
              />
            </div>
          </StaggerItem>
        ) : null}

        <ShowcaseSection
          id="use-cases"
          title={t('blog.categories.use-case')}
          posts={useCases}
          fmt={fmt}
        />
        <ShowcaseSection
          id="learning"
          title={t('blog.categories.learning')}
          posts={learning}
          fmt={fmt}
        />
        <ShowcaseSection
          id="comparison"
          title={t('blog.categories.comparison')}
          posts={comparison}
          fmt={fmt}
        />
        <ShowcaseSection
          id="foundations"
          title={t('blog.categories.foundations')}
          posts={foundations}
          fmt={fmt}
        />
      </div>
    </PageTransition>
  );
}
